import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { apiClient } from "@/lib/api";
import { FolderOpen, Image as ImageIcon, Video, MessageSquare, Users, Quote, AlertCircle, Activity } from "lucide-react";
import StatCard from "@/components/admin/StatCard";
import { PageHeader, Card } from "@/components/admin/ui";

export default function DashboardPage() {
    const [data, setData] = useState(null);

    useEffect(() => {
        apiClient.get("/dashboard/summary").then((r) => setData(r.data)).catch(() => {});
    }, []);

    const c = data?.counts || {};

    return (
        <>
            <PageHeader title="Dashboard" subtitle="A quick overview of your studio." />

            <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-4 md:gap-5">
                <StatCard testId="stat-projects" label="Projects" value={c.projects} icon={FolderOpen} />
                <StatCard testId="stat-gallery" label="Gallery" value={c.gallery} icon={ImageIcon} />
                <StatCard testId="stat-videos" label="Videos" value={c.videos} icon={Video} />
                <StatCard testId="stat-inquiries" label="Inquiries" value={c.inquiries} icon={MessageSquare} />
                <StatCard testId="stat-users" label="Users" value={c.users} icon={Users} />
                <StatCard testId="stat-testimonials" label="Testimonials" value={c.testimonials} icon={Quote} />
            </div>

            {(c.pending_inquiries > 0 || c.pending_testimonials > 0) && (
                <Card className="p-5 mt-6 border-walnut/40 bg-walnut/5">
                    <div className="flex items-start gap-3">
                        <AlertCircle size={18} className="text-walnut mt-0.5" />
                        <div className="text-sm">
                            {c.pending_inquiries > 0 && <span><strong>{c.pending_inquiries}</strong> unanswered inquir{c.pending_inquiries === 1 ? "y" : "ies"}. </span>}
                            {c.pending_testimonials > 0 && <span><strong>{c.pending_testimonials}</strong> testimonial{c.pending_testimonials === 1 ? "" : "s"} awaiting approval. </span>}
                            <Link to="/admin/inquiries" className="underline ml-1">Review now</Link>
                        </div>
                    </div>
                </Card>
            )}

            <div className="grid lg:grid-cols-2 gap-6 mt-8">
                <Card className="p-5 md:p-6" data-testid="recent-activity">
                    <div className="flex items-center gap-2 mb-4">
                        <Activity size={16} className="text-walnut" />
                        <div className="font-serif text-xl">Recent Activity</div>
                    </div>
                    <div className="space-y-3">
                        {(data?.recent_activity || []).slice(0, 10).map((a) => (
                            <div key={a.id} className="flex items-start gap-3 text-sm border-b border-stone-border dark:border-stone-800 last:border-0 pb-3 last:pb-0">
                                <div className="w-1.5 h-1.5 rounded-full bg-walnut mt-2 shrink-0" />
                                <div className="flex-1 min-w-0">
                                    <div className="text-ink dark:text-stone-100"><span className="font-medium">{a.actor}</span> {a.action}{a.target && <span className="text-stone-muted dark:text-stone-400"> · {a.target}</span>}</div>
                                    <div className="text-xs text-stone-muted dark:text-stone-500 mt-0.5">{new Date(a.created_at).toLocaleString()}</div>
                                </div>
                            </div>
                        ))}
                        {!data?.recent_activity?.length && <div className="text-sm text-stone-muted">No activity yet.</div>}
                    </div>
                </Card>

                <Card className="p-5 md:p-6">
                    <div className="flex items-center justify-between mb-4">
                        <div className="font-serif text-xl">Latest Inquiries</div>
                        <Link to="/admin/inquiries" className="text-xs text-walnut hover:text-walnut-light">View all →</Link>
                    </div>
                    <div className="space-y-3">
                        {(data?.recent_inquiries || []).slice(0, 5).map((i) => (
                            <div key={i.id} className="border-b border-stone-border dark:border-stone-800 last:border-0 pb-3 last:pb-0">
                                <div className="flex items-baseline justify-between gap-3">
                                    <div className="font-medium text-ink dark:text-stone-100 text-sm">{i.name}</div>
                                    <div className="text-xs text-stone-muted dark:text-stone-500">{new Date(i.created_at).toLocaleDateString()}</div>
                                </div>
                                <div className="text-xs text-stone-muted mt-1">{i.service || "General"} · {i.email}</div>
                                <div className="text-sm text-stone-700 dark:text-stone-300 mt-2 line-clamp-2">{i.message}</div>
                            </div>
                        ))}
                        {!data?.recent_inquiries?.length && <div className="text-sm text-stone-muted">No inquiries yet.</div>}
                    </div>
                </Card>
            </div>
        </>
    );
}
